/**
 * Node customizado para Encerrar Fluxo (end_flow)
 *
 * Diferente do node `end` (protegido/automático), este node é arrastável
 * da palette e configurável:
 *  - Mensagem opcional de despedida
 *  - Opção de fechar a conversa
 *
 * RELEASE: 1.0.0 - 2026-05-18 - Implementação inicial (gatilho inatividade)
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { PhoneOff, MessageSquare, XCircle } from 'lucide-react';
import { FlowNodeData, EndFlowConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { Badge } from '@/components/ui/badge';

/**
 * Componente visual do node de encerramento configurável
 */
export function EndFlowNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = (nodeData.config || {}) as EndFlowConfig;

  // Callbacks injetados via FlowCanvas (mesmo padrão de DelayNode/MessageTextNode)
  // Sem isso o botão "Editar" do toolbar fica inerte e o modal nunca abre.
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;

  const hasMessage = !!config.farewell_message?.trim();
  const willClose = !!config.close_conversation;

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-lg border-2 border-destructive min-w-[200px]">
      {/* Toolbar flutuante padrão (editar, duplicar, excluir) */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType="end_flow"
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />

      <Handle
        type="target"
        position={Position.Top}
        className="!bg-destructive !border-destructive-foreground !w-3 !h-3"
      />

      <div className="flex items-center gap-2 p-3">
        <div className="bg-destructive/10 text-destructive p-2 rounded">
          <PhoneOff className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">Encerrar Fluxo</div>
          <div className="text-xs text-muted-foreground truncate">
            {config.nickname || 'Finaliza execução do flow'}
          </div>
        </div>
      </div>

      {/* Badges informativos */}
      {(hasMessage || willClose) && (
        <div className="flex flex-wrap gap-1 px-3 pb-3">
          {hasMessage && (
            <Badge variant="secondary" className="text-[10px] gap-1">
              <MessageSquare className="h-3 w-3" />
              Despedida
            </Badge>
          )}
          {willClose && (
            <Badge variant="destructive" className="text-[10px] gap-1">
              <XCircle className="h-3 w-3" />
              Fecha conversa
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
